/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Bounded authorization state for the authenticated user's latest Platform MCP connection.
 */
export const ConnectionAuthState = {
  NotConnected: "not_connected",
  Active: "active",
  ReauthorizationRequired: "reauthorization_required",
} as const;
/**
 * Bounded authorization state for the authenticated user's latest Platform MCP connection.
 */
export type ConnectionAuthState = OpenEnum<typeof ConnectionAuthState>;

/**
 * Bounded package-publication state for the selected project's Default plugin after a distribution change.
 */
export const DistributionPublicationState = {
  Unknown: "",
  Pending: "pending",
  Current: "current",
  RepairRequired: "repair_required",
} as const;
/**
 * Bounded package-publication state for the selected project's Default plugin after a distribution change.
 */
export type DistributionPublicationState = OpenEnum<
  typeof DistributionPublicationState
>;

/**
 * Bounded lifecycle state for the selected MCP's existing-Default-plugin distribution.
 */
export const DistributionState = {
  Unknown: "",
  Attached: "attached",
  Removed: "removed",
} as const;
/**
 * Bounded lifecycle state for the selected MCP's existing-Default-plugin distribution.
 */
export type DistributionState = OpenEnum<typeof DistributionState>;

/**
 * Whether selected-registration readiness is currently fresh.
 */
export const ReadinessFreshness = {
  Unknown: "",
  Fresh: "fresh",
  Stale: "stale",
  Unavailable: "unavailable",
} as const;
/**
 * Whether selected-registration readiness is currently fresh.
 */
export type ReadinessFreshness = OpenEnum<typeof ReadinessFreshness>;

/**
 * Bounded current readiness state for the selected registration.
 */
export const ReadinessState = {
  Unknown: "",
  Ready: "ready",
  NeedsProviderSetup: "needs_provider_setup",
  NeedsGramAuthorization: "needs_gram_authorization",
  NeedsConfiguration: "needs_configuration",
  AuthFailed: "auth_failed",
  Unreachable: "unreachable",
  Unsupported: "unsupported",
  Unauthorized: "unauthorized",
  GuideUnavailable: "guide_unavailable",
  Degraded: "degraded",
} as const;
/**
 * Bounded current readiness state for the selected registration.
 */
export type ReadinessState = OpenEnum<typeof ReadinessState>;

/**
 * Bounded reason why interactive Platform MCP authorization is required again.
 */
export const ReauthorizationReason = {
  Unknown: "",
  IdleExpired: "idle_expired",
  AuthorizationExpired: "authorization_expired",
  RefreshInvalidated: "refresh_invalidated",
  AuthorizationChanged: "authorization_changed",
  Revoked: "revoked",
  SecurityReset: "security_reset",
} as const;
/**
 * Bounded reason why interactive Platform MCP authorization is required again.
 */
export type ReauthorizationReason = OpenEnum<typeof ReauthorizationReason>;

/**
 * A bounded next action when the workflow cannot progress.
 */
export const RepairAction = {
  Unknown: "",
  EnablePlatformMcp: "enable_platform_mcp",
  AuthorizePlatformMcp: "authorize_platform_mcp",
  SelectProject: "select_project",
  StartSetup: "start_setup",
  ContinueDashboardSetup: "continue_dashboard_setup",
  RetryReadiness: "retry_readiness",
  RetryRegistration: "retry_registration",
  ContactSupport: "contact_support",
  DistributeToDefaultPlugin: "distribute_to_default_plugin",
  RepairPublication: "repair_publication",
} as const;
/**
 * A bounded next action when the workflow cannot progress.
 */
export type RepairAction = OpenEnum<typeof RepairAction>;

/**
 * Current server-derived onboarding stage.
 */
export const Stage = {
  NotStarted: "not_started",
  InstallInstructions: "install_instructions",
  Authorized: "authorized",
  ConnectionReady: "connection_ready",
} as const;
/**
 * Current server-derived onboarding stage.
 */
export type Stage = OpenEnum<typeof Stage>;

/**
 * Safe, session-authenticated Platform MCP onboarding projection. It contains no provider URLs, credentials, OAuth values, setup handoffs, or internal resource identifiers.
 */
export type PlatformMCPOnboardingState = {
  /**
   * Whether the user copied Platform MCP configuration or completed an equivalent supported agent-setup action in this workflow.
   */
  agentConfigurationCopied: boolean;
  /**
   * Whether the active workflow observed a successful reviewed MCP Catalog search through Platform MCP.
   */
  catalogExplored: boolean;
  /**
   * Selected manual-install client family when a workflow is active.
   */
  clientFamily: string;
  /**
   * Bounded authorization state for the authenticated user's latest Platform MCP connection.
   */
  connectionAuthState: ConnectionAuthState;
  /**
   * Whether the authenticated user has a current authorized Platform MCP connection.
   */
  connectionAuthorized: boolean;
  /**
   * Whether the authenticated user's current connection has completed authenticated discovery.
   */
  connectionReady: boolean;
  /**
   * Whether the selected MCP is currently attached to the selected project's existing Default plugin.
   */
  distributionAttached: boolean;
  /**
   * Opaque server-issued version token required by distribute, remove, or publication-repair mutations. It binds the selected project and authenticated user without exposing internal IDs or counters.
   */
  distributionExpectedVersion: string;
  /**
   * Bounded package-publication state for the selected project's Default plugin after a distribution change.
   */
  distributionPublicationState: DistributionPublicationState;
  /**
   * Bounded lifecycle state for the selected MCP's existing-Default-plugin distribution.
   */
  distributionState: DistributionState;
  /**
   * Whether Platform MCP successfully added the workflow-bound MCP to the selected project's existing Default plugin.
   */
  distributionToolSucceeded: boolean;
  /**
   * Whether the active organization currently has the Platform MCP product feature enabled.
   */
  enabled: boolean;
  /**
   * Canonical Platform MCP endpoint URL supplied by the server.
   */
  mcpUrl: string;
  /**
   * Whether the organization already has a durable Platform MCP value outcome or an attached distribution.
   */
  organizationSetupComplete: boolean;
  /**
   * Whether selected-registration readiness is currently fresh.
   */
  readinessFreshness: ReadinessFreshness;
  /**
   * Bounded current readiness state for the selected registration.
   */
  readinessState: ReadinessState;
  /**
   * Whether Platform MCP observed a successful ready status check for the workflow-bound MCP.
   */
  readinessVerified: boolean;
  /**
   * Bounded reason why interactive Platform MCP authorization is required again.
   */
  reauthorizationReason: ReauthorizationReason;
  /**
   * Whether the server-owned local candidate is registered for the selected project.
   */
  registrationComplete: boolean;
  /**
   * A bounded next action when the workflow cannot progress.
   */
  repairAction: RepairAction;
  /**
   * Display name of the workflow-bound project, when one has been selected.
   */
  selectedProjectName: string;
  /**
   * Slug of the workflow-bound project, when one has been selected.
   */
  selectedProjectSlug: string;
  /**
   * Whether the server observed a successful normal Remote MCP tool call for the currently attached selected distribution version.
   */
  selectedUseVerified: boolean;
  /**
   * Current server-derived onboarding stage.
   */
  stage: Stage;
  /**
   * Whether the authenticated user has an active onboarding workflow in this organization.
   */
  workflowActive: boolean;
};

/** @internal */
export const ConnectionAuthState$inboundSchema: z.ZodMiniType<
  ConnectionAuthState,
  unknown
> = openEnums.inboundSchema(ConnectionAuthState);

/** @internal */
export const DistributionPublicationState$inboundSchema: z.ZodMiniType<
  DistributionPublicationState,
  unknown
> = openEnums.inboundSchema(DistributionPublicationState);

/** @internal */
export const DistributionState$inboundSchema: z.ZodMiniType<
  DistributionState,
  unknown
> = openEnums.inboundSchema(DistributionState);

/** @internal */
export const ReadinessFreshness$inboundSchema: z.ZodMiniType<
  ReadinessFreshness,
  unknown
> = openEnums.inboundSchema(ReadinessFreshness);

/** @internal */
export const ReadinessState$inboundSchema: z.ZodMiniType<
  ReadinessState,
  unknown
> = openEnums.inboundSchema(ReadinessState);

/** @internal */
export const ReauthorizationReason$inboundSchema: z.ZodMiniType<
  ReauthorizationReason,
  unknown
> = openEnums.inboundSchema(ReauthorizationReason);

/** @internal */
export const RepairAction$inboundSchema: z.ZodMiniType<RepairAction, unknown> =
  openEnums.inboundSchema(RepairAction);

/** @internal */
export const Stage$inboundSchema: z.ZodMiniType<Stage, unknown> = openEnums
  .inboundSchema(Stage);

/** @internal */
export const PlatformMCPOnboardingState$inboundSchema: z.ZodMiniType<
  PlatformMCPOnboardingState,
  unknown
> = z.pipe(
  z.object({
    agent_configuration_copied: z.boolean(),
    catalog_explored: z.boolean(),
    client_family: z.string(),
    connection_auth_state: ConnectionAuthState$inboundSchema,
    connection_authorized: z.boolean(),
    connection_ready: z.boolean(),
    distribution_attached: z.boolean(),
    distribution_expected_version: z.string(),
    distribution_publication_state: DistributionPublicationState$inboundSchema,
    distribution_state: DistributionState$inboundSchema,
    distribution_tool_succeeded: z.boolean(),
    enabled: z.boolean(),
    mcp_url: z.string(),
    organization_setup_complete: z.boolean(),
    readiness_freshness: ReadinessFreshness$inboundSchema,
    readiness_state: ReadinessState$inboundSchema,
    readiness_verified: z.boolean(),
    reauthorization_reason: ReauthorizationReason$inboundSchema,
    registration_complete: z.boolean(),
    repair_action: RepairAction$inboundSchema,
    selected_project_name: z.string(),
    selected_project_slug: z.string(),
    selected_use_verified: z.boolean(),
    stage: Stage$inboundSchema,
    workflow_active: z.boolean(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "agent_configuration_copied": "agentConfigurationCopied",
      "catalog_explored": "catalogExplored",
      "client_family": "clientFamily",
      "connection_auth_state": "connectionAuthState",
      "connection_authorized": "connectionAuthorized",
      "connection_ready": "connectionReady",
      "distribution_attached": "distributionAttached",
      "distribution_expected_version": "distributionExpectedVersion",
      "distribution_publication_state": "distributionPublicationState",
      "distribution_state": "distributionState",
      "distribution_tool_succeeded": "distributionToolSucceeded",
      "mcp_url": "mcpUrl",
      "organization_setup_complete": "organizationSetupComplete",
      "readiness_freshness": "readinessFreshness",
      "readiness_state": "readinessState",
      "readiness_verified": "readinessVerified",
      "reauthorization_reason": "reauthorizationReason",
      "registration_complete": "registrationComplete",
      "repair_action": "repairAction",
      "selected_project_name": "selectedProjectName",
      "selected_project_slug": "selectedProjectSlug",
      "selected_use_verified": "selectedUseVerified",
      "workflow_active": "workflowActive",
    });
  }),
);

export function platformMCPOnboardingStateFromJSON(
  jsonString: string,
): SafeParseResult<PlatformMCPOnboardingState, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => PlatformMCPOnboardingState$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'PlatformMCPOnboardingState' from JSON`,
  );
}
